// /app/search — search across the released chapters' notes (BRIEFS C4.4).
//
// The words live in the address (?q=…), so the Notes page's search box and the menu both
// land here. At least 2 letters. Up to 30 places; each card shows where it is (class,
// chapter, unit, NCERT page) and a short piece of the text with the words marked. Clicking
// a card opens the notes at that point (/app/notes/<chapter>?at=<code>).
// Events: `notes_search` once per search (details: q, results) and once when a place is
// opened (details: q, opened).
// C11.9: approved videos whose title / topics / description hold the words come first as a
// "Videos" group (one text + icon line per video: "Concept video: …" / "Question video: …",
// C12.1); clicking one plays it here (VideoModal) from its start time
// (`notes_search` with details {q, opened_video}; the player sends the video events).
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { track, useActivityArea } from '../../components/app/activity';
import { searchNotes } from '../../api/v2';
import { IconChevronRight, IconSearch } from '../../components/app/icons';
import { C, FONT, button } from '../../components/app/theme';
import { LoadError } from '../../components/app/LoadState';
import VideoModal from '../../components/app/VideoModal';
import { KIND_LABEL, VideoIcon, clipKind } from '../../components/app/VideoLines';

const MIN = 2;

// Mark every place where the words appear (case does not matter). Plain text in, React out.
function Marked({ text, q }) {
  if (!q) return text;
  const lower = text.toLowerCase();
  const needle = q.toLowerCase();
  const out = [];
  let i = 0;
  let k = 0;
  while (i < text.length) {
    const j = lower.indexOf(needle, i);
    if (j < 0) { out.push(text.slice(i)); break; }
    if (j > i) out.push(text.slice(i, j));
    out.push(<mark key={k++} style={{ background: C.marker, color: 'inherit', padding: '0 1px', borderRadius: 2 }}>{text.slice(j, j + needle.length)}</mark>);
    i = j + needle.length;
  }
  return out;
}

function where(r) {
  const unit = [r.unit_number, r.unit_title].filter(Boolean).join(' ');
  return `Class ${r.class_level} · Chapter ${r.ncert_no} · ${unit}`;
}

function ResultCard({ r, q, onOpen }) {
  return (
    <button type="button" onClick={() => onOpen(r)} data-testid={`v2-search-result-${r.at}`} style={{
      width: '100%', textAlign: 'left', cursor: 'pointer', boxSizing: 'border-box',
      minHeight: 64, padding: '14px 16px', background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 12, display: 'flex', alignItems: 'center', gap: 14, fontFamily: FONT.ui, color: C.text,
    }}>
      <div style={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.4 }}>
          {where(r)}
          {r.page ? <span> · NCERT p. {r.page}</span> : <span> · Extra</span>}
        </div>
        <div style={{ fontFamily: FONT.notes, fontSize: 17, lineHeight: 1.5 }}>
          {r.point_code
            ? <Marked text={r.snippet} q={q} />
            : <span><span style={{ color: C.muted }}>Topic: </span><strong><Marked text={r.unit_title} q={q} /></strong></span>}
        </div>
      </div>
      <IconChevronRight style={{ color: C.muted, flexShrink: 0 }} />
    </button>
  );
}


function VideoCard({ v, q, onOpen }) {
  const unit = [v.unit_number, v.unit_title].filter(Boolean).join(' ');
  return (
    <button type="button" onClick={() => onOpen(v)} data-testid={`v2-search-video-${v.id}`} style={{
      width: '100%', textAlign: 'left', cursor: 'pointer', boxSizing: 'border-box',
      minHeight: 64, padding: '14px 16px', background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 12, display: 'flex', alignItems: 'center', gap: 14, fontFamily: FONT.ui, color: C.text,
    }}>
      <span style={{ color: C.primaryDark, display: 'flex', flexShrink: 0 }}><VideoIcon kind={clipKind(v)} size={26} /></span>
      <div style={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.4 }}>
          Class {v.class_level} · Chapter {v.ncert_no} · {unit}
        </div>
        <div data-testid="v2-video-line" data-kind={clipKind(v)}
          style={{ fontSize: 17, lineHeight: 1.4, fontWeight: 600, color: C.primaryDark }}>
          {KIND_LABEL[clipKind(v)]}: <Marked text={v.title} q={q} />{v.minutes ? ` · ${v.minutes} min` : ''}
        </div>
        {v.snippet && <div style={{ fontSize: 15, lineHeight: 1.5, color: C.muted }}><Marked text={v.snippet} q={q} /></div>}
      </div>
      <IconChevronRight style={{ color: C.muted, flexShrink: 0 }} />
    </button>
  );
}

export default function AppSearch() {
  useActivityArea('notes');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = (params.get('q') || '').trim();
  const [box, setBox] = useState(q);
  const inputRef = useRef(null);
  const tracked = useRef('');
  const [video, setVideo] = useState(null);

  useEffect(() => { setBox(q); }, [q]);
  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);

  const enabled = q.length >= MIN;
  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['v2-search', q],
    queryFn: () => searchNotes(q),
    enabled,
    staleTime: 60000,
  });

  useEffect(() => {
    if (data && !data.too_short && tracked.current !== data.q) {
      tracked.current = data.q;
      track('notes_search', { details: { q: data.q, results: data.results.length, more: !!data.more, videos: (data.videos || []).length } });
    }
  }, [data]);

  const submit = (e) => {
    e.preventDefault();
    const v = box.trim();
    setParams(v ? { q: v } : {});
  };

  const open = (r) => {
    track('notes_search', {
      chapter_id: r.chapter_id, section_code: r.unit_code, point_code: r.point_code || undefined,
      details: { q, opened: r.at },
    });
    navigate(`/app/notes/${r.chapter_id}?at=${encodeURIComponent(r.at)}`);
  };

  const openVideo = (v) => {
    track('notes_search', {
      chapter_id: v.chapter_id, section_code: v.unit_code, video_clip_id: v.id,
      details: { q, opened_video: v.id },
    });
    setVideo(v);
  };

  let body = null;
  if (!q) {
    body = <p style={{ margin: 0, color: C.muted, fontSize: 15 }}>Type a word from the notes, then press Search.</p>;
  } else if (!enabled) {
    body = <p style={{ margin: 0, color: C.muted, fontSize: 15 }}>Type at least 2 letters.</p>;
  } else if (isError) {
    const busy = error && error.response && error.response.status === 429;
    body = busy
      ? <LoadError onRetry={refetch} text="You searched a lot. Wait one minute, then press Try again." />
      : <LoadError onRetry={refetch} />;
  } else if (!data || (isFetching && data.q !== q)) {
    body = <p style={{ margin: 0, color: C.muted, fontSize: 15 }}>Searching…</p>;
  } else if (data.results.length === 0 && !(data.videos || []).length) {
    body = (
      <div style={{ padding: 20, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, fontSize: 15, color: C.muted }}>
        Nothing found for “{data.q}”. Try other words.
      </div>
    );
  } else {
    const n = data.results.length;
    const vids = data.videos || [];
    body = (
      <>
        {vids.length > 0 && (
          <section data-testid="v2-search-videos" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <h2 style={{ fontFamily: FONT.heading, margin: 0, fontSize: 19, fontWeight: 600 }}>Videos</h2>
            {vids.map((v) => <VideoCard key={v.id} v={v} q={data.q} onOpen={openVideo} />)}
          </section>
        )}
        {vids.length > 0 && n > 0 && (
          <h2 style={{ fontFamily: FONT.heading, margin: '6px 0 0', fontSize: 19, fontWeight: 600 }}>In the notes</h2>
        )}
        <p data-testid="v2-search-count" style={{ margin: 0, color: C.muted, fontSize: 15 }}>
          {n === 0
            ? 'Nothing found in the notes.'
            : data.more
              ? `Showing the first ${n} places. Add more words to find fewer.`
              : `${n} ${n === 1 ? 'place' : 'places'} found.`}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {data.results.map((r) => <ResultCard key={r.at} r={r} q={data.q} onOpen={open} />)}
        </div>
      </>
    );
  }

  return (
    <div style={{ maxWidth: 820, display: 'flex', flexDirection: 'column', gap: 18 }}>
      <h1 style={{ fontFamily: FONT.heading, margin: 0, fontSize: 26, fontWeight: 600 }}>Search the notes</h1>
      <form onSubmit={submit} role="search" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <label style={{
          flex: '1 1 320px', display: 'flex', alignItems: 'center', gap: 10, minHeight: 46,
          padding: '0 14px', border: '1px solid #CBD5E1', borderRadius: 10, background: C.surface,
        }}>
          <IconSearch style={{ color: C.muted }} />
          <input ref={inputRef} type="search" value={box} onChange={(e) => setBox(e.target.value)}
            placeholder="Search notes" aria-label="Search notes" data-testid="v2-search-input"
            style={{ flexGrow: 1, border: 0, outline: 0, background: 'transparent', fontFamily: FONT.ui, fontSize: 16, color: C.text }} />
        </label>
        <button type="submit" style={button.primary} data-testid="v2-search-go">Search</button>
      </form>
      {body}
      {video && (
        <VideoModal clip={video} chapterId={video.chapter_id} unitCode={video.unit_code}
          unitLabel={[video.unit_number, video.unit_title].filter(Boolean).join(' ')}
          onClose={() => setVideo(null)} />
      )}
    </div>
  );
}
